/**
 * schainParse.js — supply-chain (schain) object parsing for privacy/identity
 * debugging. Accepts either the serialized `schain` param format
 * ("1.0,1!asi,sid,hp,rid,name,domain!...") or an ORTB schain object
 * ({ver, complete, nodes[]}), optionally wrapped as {schain: {...}}.
 *
 * Never throws — unparseable input returns {ok: false, issues: [...]}.
 */

const NODE_FIELDS = ['asi', 'sid', 'hp', 'rid', 'name', 'domain'];

function emptyResult() {
  return { ok: false, complete: null, ver: null, nodes: [], issues: [] };
}

function validateNodes(nodes, issues) {
  nodes.forEach((n, i) => {
    if (!n.asi) issues.push(`node ${i}: missing asi`);
    if (!n.sid) issues.push(`node ${i}: missing sid`);
  });
}

/** Parse the serialized schain param format. */
function parseSerialized(str) {
  const out = emptyResult();
  const parts = String(str).split('!');
  if (parts.length < 1 || !parts[0]) {
    out.issues.push('unparseable schain string');
    return out;
  }
  const header = parts[0].split(',');
  const ver = header[0] || null;
  const completeRaw = header[1];
  const complete = completeRaw === '1' ? true : completeRaw === '0' ? false : null;

  out.ver = ver;
  out.complete = complete;

  if (ver !== '1.0') {
    out.issues.push(`unknown ver: ${ver}`);
  }

  const nodeParts = parts.slice(1).filter((p) => p !== '');
  const nodes = nodeParts.map((np) => {
    const fields = np.split(',').map((f) => {
      try { return decodeURIComponent(f); } catch { return f; }
    });
    const node = {};
    NODE_FIELDS.forEach((name, idx) => {
      if (fields[idx] !== undefined && fields[idx] !== '') node[name] = fields[idx];
    });
    return node;
  });

  out.nodes = nodes;
  validateNodes(nodes, out.issues);

  if (complete === true && nodes.length === 0) {
    out.issues.push('complete=1 with empty nodes');
  }
  if (complete === false) {
    out.issues.push('chain declared incomplete');
  }

  out.ok = true;
  return out;
}

/** Parse an ORTB schain object ({ver, complete, nodes[]}), or {schain: {...}} wrapper. */
function parseOrtbObject(obj) {
  const out = emptyResult();
  const schain = obj && typeof obj === 'object' && obj.schain && typeof obj.schain === 'object'
    ? obj.schain
    : obj;

  if (!schain || typeof schain !== 'object') {
    out.issues.push('unparseable schain object');
    return out;
  }

  const ver = schain.ver != null ? String(schain.ver) : null;
  let complete = null;
  if (schain.complete === 1 || schain.complete === '1' || schain.complete === true) complete = true;
  else if (schain.complete === 0 || schain.complete === '0' || schain.complete === false) complete = false;

  out.ver = ver;
  out.complete = complete;

  if (ver !== '1.0') {
    out.issues.push(`unknown ver: ${ver}`);
  }

  const rawNodes = Array.isArray(schain.nodes) ? schain.nodes : [];
  const nodes = rawNodes.map((n) => {
    const node = {};
    if (!n || typeof n !== 'object') return node;
    NODE_FIELDS.forEach((name) => {
      if (n[name] !== undefined && n[name] !== '') node[name] = n[name];
    });
    return node;
  });

  out.nodes = nodes;
  validateNodes(nodes, out.issues);

  if (complete === true && nodes.length === 0) {
    out.issues.push('complete=1 with empty nodes');
  }
  if (complete === false) {
    out.issues.push('chain declared incomplete');
  }

  out.ok = true;
  return out;
}

/**
 * @param {string|object} input
 * @returns {{ok: boolean, complete: boolean|null, ver: string|null, nodes: Array, issues: string[]}}
 */
export function parseSchain(input) {
  try {
    if (input == null) {
      const out = emptyResult();
      out.issues.push('empty input');
      return out;
    }
    if (typeof input === 'string') {
      const s = input.trim();
      if (!s) {
        const out = emptyResult();
        out.issues.push('empty input');
        return out;
      }
      // Could be a serialized schain OR a JSON string of an ORTB object.
      if (s[0] === '{') {
        const parsed = JSON.parse(s); // let outer try/catch handle failure
        return parseOrtbObject(parsed);
      }
      return parseSerialized(s);
    }
    if (typeof input === 'object') {
      return parseOrtbObject(input);
    }
    const out = emptyResult();
    out.issues.push('unsupported input type');
    return out;
  } catch (e) {
    const out = emptyResult();
    out.issues.push(`parse error: ${e && e.message ? e.message : String(e)}`);
    return out;
  }
}

// ── row scanning ─────────────────────────────────────────────────────────

const BODY_SCHAIN_PATHS = [
  ['source', 'ext', 'schain'],
  ['ext', 'schain'],
];

function getAtPath(obj, path) {
  let cur = obj;
  for (const key of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[key];
  }
  return cur;
}

function getBodyObject(row) {
  const rb = row && row.requestBody;
  if (rb && typeof rb === 'object') return rb;
  if (typeof rb === 'string') {
    const s = rb.trim();
    if (!s || s[0] !== '{') return null;
    if (s.length > 256 * 1024) return null;
    try { return JSON.parse(s); } catch { return null; }
  }
  return null;
}

function hostOf(url) {
  try { return new URL(url).host; } catch { return 'unknown-host'; }
}

/**
 * Scan rows for schain declarations (query param `schain`, or body paths
 * source.ext.schain / ext.schain / per-bidder ext.<bidder>.schain).
 * Dedups by serialized value.
 * @param {Array<object>} rows
 * @returns {Array<{host: string, requestType: string, parsed: object}>}
 */
export function findSchains(rows) {
  const out = [];
  if (!Array.isArray(rows)) return out;
  const seen = new Set();

  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    let host = 'unknown-host';
    const requestType = row.requestType || 'unknown';

    try {
      if (row.url) host = hostOf(row.url);
    } catch { /* ignore */ }

    const candidates = [];

    // query param
    try {
      const u = new URL(row.url);
      const qp = u.searchParams.get('schain');
      if (qp) candidates.push(qp);
    } catch { /* ignore */ }

    // body paths
    try {
      const body = getBodyObject(row);
      if (body && typeof body === 'object') {
        for (const path of BODY_SCHAIN_PATHS) {
          const val = getAtPath(body, path);
          if (val) candidates.push(val);
        }
        // per-bidder: ext.<bidder>.schain
        const ext = getAtPath(body, ['ext']);
        if (ext && typeof ext === 'object') {
          for (const key of Object.keys(ext)) {
            const bidderSchain = getAtPath(ext, [key, 'schain']);
            if (bidderSchain) candidates.push(bidderSchain);
          }
        }
      }
    } catch { /* ignore */ }

    for (const candidate of candidates) {
      let dedupeKey;
      try {
        dedupeKey = typeof candidate === 'string' ? candidate : JSON.stringify(candidate);
      } catch {
        dedupeKey = String(candidate);
      }
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);

      const parsed = parseSchain(candidate);
      out.push({ host, requestType, parsed });
    }
  }

  return out;
}
